import { ElementRef, Input, Inject, OnInit, Component, Output, EventEmitter } from '@angular/core';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { PermissionSchemaConstants } from '../../constants/permission-schema.constants';

@Component({
  selector: '[genPermissionSchemaRestrict]',
  template: `<ng-content></ng-content>`
})

export class PermissionSchemaRestrictComponent implements OnInit {

  public static actionHide: string = 'hide';
  public static actionDisable: string = 'disable';

  // the key of the permission schema (as is mapped in PermissionSchemaConstants) to check
  @Input() permissionSchemaKey: string | Array<string>;

  // the action to run (disable / hide) in case that user is not permitted for the given permission schema
  @Input() nonePermissionAction: string;

  // an alternate message to display in case that user is not permitted for the given permission schema
  @Input() nonePermissionOnHideMsg: string;

  // the class name of the alternate message to display in case that user is not permitted for the given permission schema
  @Input() nonePermissionOnHideMsgClass: string;

  // indicates if to force the permission.
  // This flag can be used in case that the permission's check is conditioned
  @Input() forcePermission: boolean = true;

  // the emitter will emit the allow status of the check so any parent will have the ability to catch the state
  @Output()
  notifyPermissionState: EventEmitter<boolean> = new EventEmitter();

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  constructor(private authService: AuthenticationService,
              private element: ElementRef) {

  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  ngOnInit() {
    this.applyCheck();
  }

  /**
   * check if user has permission for the given schemaKey. If not, run the operation to perform in such a case
   */
  applyCheck() {

    // if it is implicitly indicated not to force the permission - exit
    if (this.forcePermission === false) return;
    if (!this.permissionSchemaKey) return;
    let schemaIsPermitted: boolean = false;
    // resolve the permission schema value by the given key
    if (Array.isArray(this.permissionSchemaKey)) {
        const schemaPermittedCheck: Array<boolean> = [];
        this.permissionSchemaKey.forEach((element, index) => {
          const permissionSchema: string = PermissionSchemaConstants.getPermissionSchema(this.permissionSchemaKey[index]);
          if (permissionSchema == null) {
            console.error('Invalid permissionSchemaKey for genPermissionSchemaRestrict directive: ' + this.permissionSchemaKey[index]);
            return;
          }
          // check if user has permission to the given schema
          schemaPermittedCheck.push(this.authService.doesUserHasPermission(permissionSchema));
        });
      schemaIsPermitted = schemaPermittedCheck.every(Boolean);
    } else {
      const permissionSchema: string = PermissionSchemaConstants.getPermissionSchema(this.permissionSchemaKey);
      if (permissionSchema == null) {
        console.error('Invalid permissionSchemaKey for genPermissionSchemaRestrict directive: ' + this.permissionSchemaKey);
        return;
      }
      // check if user has permission to the given schema
       schemaIsPermitted = this.authService.doesUserHasPermission(permissionSchema);
    }

    // update the container regarding the current permission state
    this.notifyPermissionState.emit(schemaIsPermitted);

    // if user is permitted for the given schema - nothing to do, exit
    if (schemaIsPermitted) {
      return;
    }

    // user is not permitted - run the action to operate:

    // if action indicates to disable element OR no action value is provided - disable this element and its child (default action):
    if (this.isEmpty(this.nonePermissionAction) ||
        this.nonePermissionAction === PermissionSchemaRestrictComponent.actionDisable) {

        this.doDisable();

    // if action indicates to hide element - hide this element and its child:
    } else if (this.nonePermissionAction === PermissionSchemaRestrictComponent.actionHide) {

        this.doHide();

    // unknown value - show an error
    } else {
      console.error('Invalid action value for genPermissionSchemaRestrict directive: ' + this.nonePermissionAction);

    }

  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * hide attached control
   */
  private doHide(): void {

    const currentElement: any = this.element.nativeElement;

    // before hiding the element, see if there is a message to display instead of this element
    this.displayMessageOnHide(currentElement);

    currentElement.style.display = 'none';
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * disable attached control
   */
  private doDisable(): void {

    this.disableElementAndItsChild(this.element.nativeElement);
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private disableElementAndItsChild(currentElement: any) {

    // change cursor type
    currentElement.style.cursor = 'not-allowed';
    currentElement.style.pointerEvents = 'none';
    // if element supports the 'disabled' property and it is currently enabled- set 'disabled' to true
    if (currentElement.disabled !== undefined) {
      this.showTooltip(currentElement);
      currentElement.disabled = 'disabled';

    } else {

      if (currentElement === this.element.nativeElement) {
        this.showTooltip(currentElement);
      }

      // revoke any pointer events that are associated to this element to disable it in practice
      currentElement.style.pointerEvents = 'none';

      // currentElement.style.textDecoration = 'none';
      // currentElement.style.color = '#848484';
      // currentElement.style.cursor = 'not-allowed';
      // currentElement.style.opacity = '0.65';
    }

    // Note: though that when setting the style properties on the parent element it applies it also on the child elements,
    //       there is a need to go over all child for setting the <disabled> on the relevant elements so they will appear
    //       as disabled
    if (currentElement.children && currentElement.children.length > 0) {
      for (const el of currentElement.children) {
        this.disableElementAndItsChild(el);
      }
    }

  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private showTooltip(currentElement: any): void {

    // show a tooltip on a disabled element to indicate that user does not have permissions for this element.
    // the tooltip is displayed for any element that supports the <disabled> attribute or on the target element (to avoid
    //  displaying it on each child element)
    let title: string = '[User has no permissions]';
    if (! this.isEmpty(currentElement.title)) {
      title = currentElement.title + ' ' + title;
    } else if (! this.isEmpty(currentElement.tooltip)) {
      title = currentElement.tooltip + ' ' + title;
    }

    currentElement.setAttribute('data-toggle', 'tooltip');
    currentElement.setAttribute('data-placement', 'top');
    currentElement.title = title;
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private displayMessageOnHide(currentElement: any) {

    // if no message to display when hiding element - exit
    if (this.isEmpty(this.nonePermissionOnHideMsg)) {
      return;
    }

    // create a new SPAN element and append it right after the current element
    const spanElement = currentElement.ownerDocument.createElement('span');
    currentElement.parentNode.insertBefore(spanElement, currentElement.nextSibling);

    spanElement.innerHTML = this.nonePermissionOnHideMsg;

    // if preferred class name is provided:
    if (! this.isEmpty(this.nonePermissionOnHideMsgClass)) {
      spanElement.className = this.nonePermissionOnHideMsgClass;
    }

  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private isEmpty(value: string): boolean {
    return value == null || value === undefined || value.length === 0;
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

}


